import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { AssetCard, SpriteIndex } from '@/lib/api/types';
import { fetchJson } from '@/lib/api/client';
import { keys } from '@/lib/api/keys';
import { queryClient } from '@/lib/api/queryClient';
import { spriteStyleAt } from '@/lib/media/sprite';
import { msToClock } from '@/lib/media/timecode';
import { ScrubVideoController } from './ScrubVideoController';

export function fetchSpriteIndex(versionId: string, url: string): Promise<SpriteIndex> {
  return queryClient.fetchQuery({ queryKey: keys.spriteIndex(versionId), queryFn: () => fetchJson<SpriteIndex>(url), staleTime: Infinity, gcTime: 30 * 60_000 });
}

type Refs = {
  media: React.RefObject<HTMLDivElement | null>;
  scrub: React.RefObject<HTMLDivElement | null>;
  line: React.RefObject<HTMLDivElement | null>;
  time: React.RefObject<HTMLSpanElement | null>;
};

/**
 * §2.9 hover-scrub. All per-move work mutates DOM through refs inside one rAF; React never
 * re-renders on pointermove. Returns pointer handlers for the card media box.
 */
export function useSpriteScrub(card: AssetCard, refs: Refs) {
  const idx = useRef<SpriteIndex | null>(null);
  const rect = useRef<DOMRect | null>(null);
  const raf = useRef(0);
  const pending = useRef<number | null>(null);
  const active = useRef(false);
  const mode = useRef<'sprite' | 'video' | 'none'>('none');
  const versionId = card.active_version_id;
  const spriteUrl = card.sprite_sheet_url ?? null;
  const indexUrl = card.sprite_index_url ?? null;
  const previewUrl = card.preview_mp4_url ?? null;
  const duration = card.duration_ms ?? 0;
  // M6: slot recycling swaps the card under a live scrub; the time badge must read the current duration
  const durationRef = useRef(duration);
  useLayoutEffect(() => {
    durationRef.current = duration;
  });
  const enabled = card.type === 'video' && card.version_state === 'ready' && (!!indexUrl || (!!previewUrl && duration >= 1000));

  useEffect(() => {
    idx.current = null;
    mode.current = 'none';
  }, [versionId]);

  const apply = useCallback(() => {
    raf.current = 0;
    const xr = pending.current;
    if (xr == null || !active.current) return;
    const r = rect.current;
    if (!r) return;
    if (mode.current === 'sprite' && idx.current && refs.scrub.current) {
      const st = spriteStyleAt(idx.current, xr, r.width);
      const el = refs.scrub.current;
      el.style.backgroundSize = st.backgroundSize;
      el.style.backgroundPosition = st.backgroundPosition;
      if (refs.time.current) refs.time.current.textContent = msToClock(st.t_ms);
    } else if (mode.current === 'video') {
      ScrubVideoController.seek(xr);
      if (refs.time.current) refs.time.current.textContent = msToClock(xr * durationRef.current);
    }
    if (refs.line.current) refs.line.current.style.transform = `scaleX(${xr})`;
  }, [refs]);

  const onPointerEnter = useCallback(() => {
    if (!enabled || !refs.media.current) return;
    active.current = true;
    rect.current = refs.media.current.getBoundingClientRect();
    if (indexUrl && spriteUrl) {
      if (idx.current) {
        mode.current = 'sprite';
        if (refs.scrub.current) {
          refs.scrub.current.style.backgroundImage = `url("${spriteUrl}")`;
          refs.scrub.current.hidden = false;
        }
      } else {
        fetchSpriteIndex(versionId, indexUrl).then(
          (i) => {
            if (!active.current) return;
            idx.current = i;
            mode.current = 'sprite';
            if (refs.scrub.current) {
              refs.scrub.current.style.backgroundImage = `url("${spriteUrl}")`;
              refs.scrub.current.hidden = false;
            }
            if (pending.current != null && !raf.current) raf.current = requestAnimationFrame(apply);
          },
          () => {
            /* poster stays */
          },
        );
      }
    } else if (previewUrl && duration >= 1000) {
      mode.current = 'video';
      ScrubVideoController.attach(refs.media.current, previewUrl, duration);
    }
  }, [enabled, indexUrl, spriteUrl, previewUrl, duration, versionId, refs, apply]);

  const onPointerMove = useCallback(
    (e: { clientX: number }) => {
      if (!active.current || mode.current === 'none') return;
      const r = rect.current;
      if (!r) return;
      const xr = Math.min(0.9999, Math.max(0, (e.clientX - r.left) / r.width));
      pending.current = xr;
      if (!raf.current) raf.current = requestAnimationFrame(apply);
    },
    [apply],
  );

  const onPointerLeave = useCallback(() => {
    active.current = false;
    pending.current = null;
    if (raf.current) {
      cancelAnimationFrame(raf.current);
      raf.current = 0;
    }
    if (refs.scrub.current) refs.scrub.current.hidden = true;
    if (refs.line.current) refs.line.current.style.transform = 'scaleX(0)';
    if (refs.time.current) refs.time.current.textContent = msToClock(durationRef.current);
    if (mode.current === 'video' && refs.media.current) ScrubVideoController.detach(refs.media.current);
  }, [refs]);

  // touch: horizontal intent scrubs and suppresses click
  const touchStart = useRef<{ x: number; y: number; scrubbing: boolean } | null>(null);
  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (!t || !enabled) return;
      touchStart.current = { x: t.clientX, y: t.clientY, scrubbing: false };
    },
    [enabled],
  );
  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const t = e.touches[0];
      const s = touchStart.current;
      if (!t || !s) return;
      if (!s.scrubbing) {
        const dx = Math.abs(t.clientX - s.x);
        const dy = Math.abs(t.clientY - s.y);
        if (dx > 8 && dx > dy) {
          s.scrubbing = true;
          onPointerEnter();
        } else return;
      }
      e.preventDefault();
      onPointerMove(t);
    },
    [onPointerEnter, onPointerMove],
  );
  const onTouchEnd = useCallback(() => {
    const s = touchStart.current;
    touchStart.current = null;
    if (s?.scrubbing) onPointerLeave();
  }, [onPointerLeave]);
  const wasTouchScrubbing = useCallback(() => !!touchStart.current?.scrubbing, []);

  useEffect(() => () => onPointerLeave(), [onPointerLeave]);

  return { enabled, onPointerEnter, onPointerMove, onPointerLeave, onTouchStart, onTouchMove, onTouchEnd, wasTouchScrubbing };
}
